import { useMemo, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  MarkerType,
  NodeTypes,
} from '@xyflow/react';
import dagre from 'dagre';
import PhaseTaskNode from './PhaseTaskNode';
import { ITask } from '@/types/task';
import '@xyflow/react/dist/style.css';

interface PhaseFlowViewProps {
  tasks: ITask[];
  onEditTask: (task: ITask) => void;
  onAddPriorTask: (task: ITask) => void;
  onAddPostTask: (task: ITask) => void;
  onAddTask: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const nodeTypes: NodeTypes = {
  phaseTaskNode: PhaseTaskNode as any,
};

const NODE_WIDTH = 200;
const NODE_HEIGHT = 140;

// Use dagre to auto-layout the graph (left-to-right for dependency flow)
const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction: 'TB' | 'LR' = 'LR'
) => {
  if (nodes.length === 0) {
    return { nodes: [], edges: [] };
  }

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  dagreGraph.setGraph({
    rankdir: direction,
    nodesep: 60,   // Horizontal spacing between nodes
    ranksep: 80,   // Spacing between ranks (columns in LR)
    marginx: 40,
    marginy: 40,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const PhaseFlowView = ({
  tasks,
  onEditTask,
  onAddPriorTask,
  onAddPostTask,
  onAddTask,
}: PhaseFlowViewProps) => {
  // Build nodes and edges from tasks
  const { initialNodes, initialEdges } = useMemo(() => {
    const nodes: Node[] = tasks.map((task) => ({
      id: task.id,
      type: 'phaseTaskNode',
      data: {
        task,
        onEdit: onEditTask,
        onAddPrior: onAddPriorTask,
        onAddPost: onAddPostTask,
      },
      position: { x: 0, y: 0 }, // Will be set by dagre
      draggable: false,
    }));

    const edges: Edge[] = [];
    const taskIds = new Set(tasks.map((t) => t.id));

    tasks.forEach((task) => {
      // Create edges from prior dependencies TO this task
      if (task.prior_deps) {
        task.prior_deps.forEach((depId) => {
          if (taskIds.has(depId)) {
            edges.push({
              id: `${depId}-${task.id}`,
              source: depId,
              target: task.id,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 16,
                height: 16,
                color: '#10b981',
              },
              style: {
                strokeWidth: 2,
                stroke: '#10b981',
              },
              animated: task.status !== 'done',
            });
          }
        });
      }
    });

    // Apply dagre layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      nodes,
      edges
    );

    return { initialNodes: layoutedNodes, initialEdges: layoutedEdges };
  }, [tasks, onEditTask, onAddPriorTask, onAddPostTask]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Update nodes/edges when tasks change
  useEffect(() => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      initialNodes,
      initialEdges
    );
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const task = tasks.find((t) => t.id === node.id);
      if (task) {
        onEditTask(task);
      }
    },
    [tasks, onEditTask]
  );

  // Empty state
  if (tasks.length === 0) {
    return (
      <div className="h-[300px] bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center">
        <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <p className="text-gray-500 dark:text-gray-400 text-sm mb-3">No tasks in this phase</p>
        <button
          onClick={onAddTask}
          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add First Task
        </button>
      </div>
    );
  }

  return (
    <div className="h-[300px] bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 relative">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3, minZoom: 0.5, maxZoom: 1.2 }}
        minZoom={0.3}
        maxZoom={1.5}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#d1d5db" gap={16} size={1} />
        <Controls
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
        />
      </ReactFlow>

      {/* Legend */}
      <div className="absolute bottom-3 left-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 shadow-sm z-10">
        <div className="flex items-center gap-3 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-gray-400"></div>
            <span className="text-gray-500 dark:text-gray-400">Plan</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-blue-500"></div>
            <span className="text-gray-500 dark:text-gray-400">Doing</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-amber-500"></div>
            <span className="text-gray-500 dark:text-gray-400">Review</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded bg-emerald-500"></div>
            <span className="text-gray-500 dark:text-gray-400">Done</span>
          </div>
        </div>
      </div>

      {/* Hover hint */}
      <div className="absolute top-3 left-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 shadow-sm z-10">
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Hover nodes for <span className="text-emerald-500 font-medium">+</span> to add tasks
        </span>
      </div>
    </div>
  );
};

export default PhaseFlowView;
